import React, { useEffect, useRef } from 'react'
import ApexCharts, { ApexOptions } from 'apexcharts'
import { getCSS, getCSSVariableValue } from '../../../../../_metronic/assets/ts/_utils'
import { useShortScale } from '../../../../hooks/useShortScale'

type StatisticsData = {
  [key: string]: number
}
type Props = {
  className: string
  color: string[]
  label: string[]
  statisticsData: StatisticsData[]
}

export const MultipleStatisticsWidget: React.FC<Props> = ({ className, color, statisticsData, label }) => {
  const { convertValueToShortScale } = useShortScale()

  const chartRef = useRef<HTMLDivElement | null>(null)
  let maxValue: number
  let allZeroes = true

  // Find the max value across all datasets and check if all values are zero
  if (statisticsData) {
    const allValues = statisticsData.flatMap(Object.values)
    maxValue = Math.max(...allValues)
    const values = allValues.map(Number)
    allZeroes = values.every((val) => val === 0)
  }

  const refreshChart = () => {
    if (!chartRef.current) {
      return
    }

    const height = parseInt(getCSS(chartRef.current, 'height'))
    const labelColor = getCSSVariableValue('')

    // Prepare chart options with multiple series
    const chart = new ApexCharts(
      chartRef.current,
      getChartOptions(
        height,
        labelColor,
        color,
        statisticsData,
        maxValue,
        label,
        convertValueToShortScale
      )
    )
    if (chart) {
      chart.render()
    }

    return chart
  }

  useEffect(() => {
    const chart = refreshChart()

    return () => {
      if (chart) {
        chart.destroy()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartRef, statisticsData])

  return (
    <div className={`${allZeroes ? '' : ''} ${className}`}>
      <div className='d-flex flex-column p-0'>
        <div
          ref={chartRef}
          className='statistics-widget-3-chart card-rounded-bottom'
          style={{ height: '135px' }}
        ></div>
      </div>
    </div>
  )
}

function getChartOptions(
  height: number,
  labelColor: string,
  color: string[],
  statisticsData: StatisticsData[],
  maxValue: number,
  labels: string[],
  convertValueToShortScale: any
): ApexOptions {
  const series = statisticsData.map((data, index) => ({
    name: labels[index] || '',  // Include label names if needed
    data: Object.values(data).map(Number),
  }));

  const xaxisCategories = Object.keys(statisticsData[0]);

  const options: ApexOptions = {
    series: series,
    chart: {
      fontFamily: 'inherit',
      type: 'area',  // Change chart type to 'area' for filled areas
      height: height,
      width: '100%',
      toolbar: {
        show: false,  // No toolbar needed
      },
      zoom: {
        enabled: false,  // Disable zoom
      },
      sparkline: {
        enabled: false,  // Full chart, not a sparkline
      },
    },
    legend: {
      show: false,  // Hide legend
    },
    dataLabels: {
      enabled: false,  // No data labels
    },
    fill: {
      type: 'gradient',  // Use gradient fill to give a nice effect
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.5,  // Set the opacity of the filled area
        opacityTo: 0,  // Set the ending opacity (transparent at the top)
        stops: [0, 100]
      },
    },
    stroke: {
      curve: 'smooth',
      width: 2,
      colors: color,
    },
    markers: {
      size: 0,  // No markers (dots) on the lines
    },
    xaxis: {
      categories: xaxisCategories,
      axisBorder: {
        show: false,  // Hide X-axis border
      },
      axisTicks: {
        show: false,  // Hide X-axis ticks
      },
      labels: {
        show: false,  // Hide X-axis labels
      },
      crosshairs: {
        show: false,  // No crosshairs
      },
    },
    yaxis: {
      show: false,  // Hide Y-axis
      min: 0,
      max: maxValue,
    },
    tooltip: {
      enabled: true,  // Disable tooltips
      marker: {
        show: true,
        fillColors: color, // Apply the colors passed as props
      },

    },
    grid: {
      show: false,  // No grid lines
    },
  };

  return options;
}














